import { useState } from "react";
import { UserMenu } from "./UserMenu";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { AtendimentoEmCursoBotao } from "./AtendimentoEmCursoBotao";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { BarraSuperiorCelular } from "./mobile/BarraSuperiorCelular";
import { BotaoBusca } from "@/components/busca/BotaoBusca";
import { useAtalhoDeAtendimento } from "@/lib/em-atendimento";
import { useIsMobile } from "@/lib/use-media-query";
import { usePageHeader } from "@/lib/page-header";
import { useProductTourContext } from "@/components/product-tour/ProductTourProvider";
import { TourCallout } from "@/components/product-tour/TourCallout";
import { hasSeenTour } from "@/lib/product-tour/tour-storage";
import { DentalbookLogo } from "@/components/DentalbookLogo";

/**
 * A barra superior tem dono fixo. Desktop: o nome da página à esquerda, o switcher de organização
 * no centro, busca, sino e perfil à direita. O atalho de atendimento aparece só enquanto há um
 * atendimento em curso, porque é o único jeito de voltar ao paciente de qualquer tela. A busca é a
 * exceção à regra de "nada de botão aqui": ela serve a todas as páginas, não é botão de uma página.
 * Celular: o desenho do LinkedIn (`BarraSuperiorCelular`), sem título; o atendimento vai pra faixa
 * acima da barra de abas.
 *
 * Uma árvore OU a outra, nunca as duas escondidas por CSS: a busca registra o Ctrl+K e o sino abre
 * o stream de notificações — duplicados, o atalho se anulava e o som tocava duas vezes.
 *
 * Botão de página não entra aqui — mora na própria página. Ver `useSetPageHeader`.
 */
export function Topbar({ comLogo = false }: { comLogo?: boolean }) {
  const header = usePageHeader();
  const isMobile = useIsMobile();
  const temAtalho = !!useAtalhoDeAtendimento();
  const { start, available, justCompletedOnboarding, dismissCallout } = useProductTourContext();
  const [showBadge, setShowBadge] = useState(() => !hasSeenTour());
  const iniciarTour = () => {
    setShowBadge(false);
    start();
  };

  if (isMobile) {
    return (
      <header
        className="sticky top-0 z-40 flex items-center border-b border-border bg-white px-3 py-2 dark:bg-card"
        style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
      >
        <BarraSuperiorCelular
          tourDisponivel={available}
          mostrarSeloDoTour={showBadge}
          onIniciarTour={iniciarTour}
        />
      </header>
    );
  }

  return (
    // Três colunas com as laterais em `1fr` iguais: o meio fica centralizado de verdade e ninguém
    // invade o espaço de ninguém. O meio é sempre do switcher — ele é permanente, o atendimento é
    // passageiro, então quem muda de lugar é o atendimento.
    <header className="sticky top-0 z-40 grid h-16 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b border-border bg-white px-4 lg:px-6 dark:bg-card">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* Sem menu lateral (janela de etiquetas), o logo vem pra barra. */}
        {comLogo && <DentalbookLogo className="h-9 w-9 shrink-0" />}
        {header && (
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="truncate text-lg font-semibold leading-tight text-foreground">{header.title}</div>
            {header.subtitle && <p className="truncate text-xs text-muted-foreground">{header.subtitle}</p>}
          </div>
        )}
      </div>

      <div className="flex shrink-0 justify-center px-2">
        <OrganizationSwitcher variante="barra" />
      </div>

      <div className="flex min-w-0 items-center justify-end gap-1 lg:gap-3">
        {temAtalho && <AtendimentoEmCursoBotao compacto />}

        <BotaoBusca />
        <NotificationBell />

        {available ? (
          <TourCallout open={justCompletedOnboarding} onDismiss={dismissCallout} onStartTour={start}>
            <div className="flex items-center">
              <UserMenu tourDisponivel mostrarSeloDoTour={showBadge} onIniciarTour={iniciarTour} />
            </div>
          </TourCallout>
        ) : (
          <UserMenu />
        )}
      </div>
    </header>
  );
}
